// FILE: src/components/Admin/AI/rag/RagPipelinesPanel.tsx
import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import PipelineCard from '../kb/PipelineCard';
import PipelineFilesTable from '../kb/PipelineFilesTable';
import UploadDialog from '../kb/UploadDialog';

import useDriftKnowledgeBase from '../../../../hooks/useDriftKnowledgeBase';
import {
  normalizeItems,
  normalizeTotalCount,
  useAddRagPipelineMutation,
  useDeleteRagPipelineMutation,
  useFetchRagPipelines,
  useUpdateRagPipelineMutation,
} from '../../../../hooks/rag-hooks';

import RagPipelineEditorDialog from './RagPipelineEditorDialog';
import { dateLabel, statusChip } from './ragAdminUtils';

const providerLabel = (provider?: string) => {
  if (provider === 'drift') return 'DRIFT';
  if (provider === 'ragaas') return 'RAGaaS';
  return provider || '-';
};

const externalLabel = (row: any) => {
  if (row.provider === 'drift') return row.provider_pipeline_id || '-';
  if (row.provider === 'ragaas') return row.gcs_bucket || row.pubsub_topic || row.gcp_project || '-';
  return row.provider_pipeline_id || '-';
};

const RagPipelinesPanel: React.FC = () => {
  const [page, setPage] = React.useState(0);
  const [size, setSize] = React.useState(25);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<any | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = React.useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = React.useState(false);

  const params = React.useMemo(() => ({
    page: page + 1,
    size,
    detailed: true,
    depth: 2,
    'order-by': 'name',
  }), [page, size]);

  const query = useFetchRagPipelines(params, {
    placeholderData: keepPreviousData,
  });

  const rows = normalizeItems<any>(query.data);
  const total = normalizeTotalCount<any>(query.data, rows);

  React.useEffect(() => {
    if (selectedPipelineId || !rows.length) return;
    setSelectedPipelineId(rows[0].id);
  }, [rows, selectedPipelineId]);

  const selectedPipeline =
    rows.find(row => String(row.id) === String(selectedPipelineId)) || null;

  const providerPipelineId =
    selectedPipeline?.provider === 'drift'
      ? selectedPipeline?.provider_pipeline_id || ''
      : '';

  const kb = useDriftKnowledgeBase({
    pipelineId: providerPipelineId,
    enabled: Boolean(providerPipelineId && selectedPipeline?.provider === 'drift'),
  });

  const addMutation = useAddRagPipelineMutation();
  const updateMutation = useUpdateRagPipelineMutation();
  const deleteMutation = useDeleteRagPipelineMutation();

  const saving = addMutation.isPending || updateMutation.isPending;

  const refresh = async () => {
    await query.refetch();
    await kb.refresh();
  };

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setEditorOpen(true);
  };

  const save = async (payload: any) => {
    let saved: any;

    if (payload.id) {
      saved = await updateMutation.mutateAsync(payload);
    } else {
      saved = await addMutation.mutateAsync(payload);
    }

    setEditorOpen(false);
    setEditing(null);

    await query.refetch();

    if (saved?.id) {
      setSelectedPipelineId(Number(saved.id));
    }
  };

  const remove = async (row: any) => {
    if (!row?.id) return;

    if (!window.confirm(`Delete RAG pipeline "${row.name || row.key}"?`)) return;

    await deleteMutation.mutateAsync(row.id);

    if (String(selectedPipelineId) === String(row.id)) {
      setSelectedPipelineId(null);
    }

    await query.refetch();
  };

  const selectedIsDrift = selectedPipeline?.provider === 'drift';

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              RAG Pipelines and Knowledge Base
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a concrete RAG pipeline to inspect its provider configuration and knowledge-base state.
              DRIFT pipelines expose file listing, upload, delete, and ingestion monitoring here.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={refresh}>
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>
              Add Pipeline
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {query.error ? (
        <Alert severity="error">
          {(query.error as Error)?.message || 'Failed to load RAG pipelines.'}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} xl={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {query.isLoading || query.isFetching ? <LinearProgress sx={{ mb: 2 }} /> : null}

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Pipeline</TableCell>
                    <TableCell width={110}>Provider</TableCell>
                    <TableCell width={130}>Status</TableCell>
                    <TableCell width={90}>Secret</TableCell>
                    <TableCell width={110} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map(row => {
                    const selected = String(row.id) === String(selectedPipelineId);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        selected={selected}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedPipelineId(row.id)}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {row.name || row.key}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.key}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {externalLabel(row)}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip size="small" variant="outlined" label={providerLabel(row.provider)} />
                        </TableCell>

                        <TableCell>{statusChip(row.status)}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={row.has_secret || row.secret_name ? 'success' : 'default'}
                            label={row.has_secret || row.secret_name ? 'yes' : 'no'}
                          />
                        </TableCell>

                        <TableCell align="right" onClick={event => event.stopPropagation()}>
                          <Tooltip title="Edit pipeline">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete pipeline">
                            <IconButton size="small" color="error" onClick={() => remove(row)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {!rows.length && !query.isLoading ? (
              <Typography variant="body2" color="text.secondary" mt={2}>
                No RAG pipelines found.
              </Typography>
            ) : null}

            <TablePagination
              component="div"
              count={Number(total || 0)}
              page={page}
              rowsPerPage={size}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={event => {
                setSize(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} xl={7}>
          {!selectedPipeline ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a pipeline to view provider-specific knowledge-base context.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="h6" fontWeight={700}>
                    {selectedPipeline.name || selectedPipeline.key}
                  </Typography>
                  <Chip size="small" variant="outlined" label={providerLabel(selectedPipeline.provider)} />
                  {statusChip(selectedPipeline.status)}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Updated: ${dateLabel(selectedPipeline.last_updated)}`}
                  />
                </Stack>

                {selectedPipeline.description ? (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {selectedPipeline.description}
                  </Typography>
                ) : null}

                {selectedPipeline.provider === 'ragaas' ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    RAGaaS configuration is stored on this pipeline. Upload and metadata ingestion should use
                    the configured GCS bucket, PubSub topic, API URLs, client IDs, and AWS secret values.
                  </Alert>
                ) : null}
              </Paper>

              <PipelineCard
                provider={selectedPipeline.provider}
                pipelineId={providerPipelineId}
                pipeline={kb.pipeline}
                loading={kb.pipelineLoading}
                taskId={kb.taskId}
                executionId={kb.executionId}
                uploadStatus={kb.uploadStatus}
                ingestionStatus={kb.ingestionStatus}
                onRefresh={kb.refresh}
                onAddFiles={() => setUploadOpen(true)}
                disableUpload={!selectedIsDrift || !providerPipelineId}
              />

              <PipelineFilesTable
                pipelineId={providerPipelineId}
                files={kb.files}
                onDeleted={kb.refresh}
                disabled={!selectedIsDrift || !providerPipelineId}
              />
            </Stack>
          )}
        </Grid>
      </Grid>

      <RagPipelineEditorDialog
        open={editorOpen}
        initialValue={editing}
        saving={saving}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSubmit={save}
      />

      <UploadDialog
        open={uploadOpen}
        pipelineId={providerPipelineId}
        pipelineName={selectedPipeline?.name || selectedPipeline?.key}
        onClose={() => setUploadOpen(false)}
        onUpload={kb.uploadFiles}
        uploading={kb.uploading}
        taskId={kb.taskId}
        executionId={kb.executionId}
        uploadStatus={kb.uploadStatus}
        ingestionStatus={kb.ingestionStatus}
      />
    </Stack>
  );
};

export default RagPipelinesPanel;